
import useValidate from '@vuelidate/core'
import { defineComponent } from 'vue'
import { AxiosResponse } from 'axios'
import detailViewDescriptor from './DatasetManagementDetailViewDescriptor.json'
import DetailCard from './detailCard/DatasetManagementDetailCard.vue'
import TypeCard from './typeCard/DatasetManagementTypeCard.vue'
import AdvancedCard from './advancedCard/DatasetManagementAdvancedCard.vue'
import LinkCard from './linkCard/DatasetManagementLinkCard.vue'
import MetadataCard from './metadataCard/DatasetManagementMetadataCard.vue'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import WorkspaceDataPreviewDialog from '@/modules/workspace/views/dataView/dialogs/WorkspaceDataPreviewDialog.vue'
import { mapState } from 'vuex'
import Menu from 'primevue/menu'
import PreparedDataset from '@/modules/managers/datasetManagement/detailView/preparedDataset/DatasetManagementPreparedDataset.vue'
import QBE from '@/modules/qbe/QBE.vue'

export default defineComponent({
    components: { TabView, TabPanel, DetailCard, AdvancedCard, LinkCard, TypeCard, MetadataCard, WorkspaceDataPreviewDialog, Menu, PreparedDataset, QBE },
    props: {
        id: { type: String, required: false },
        scopeTypes: { type: Array as any, required: true },
        categoryTypes: { type: Array as any, required: true },
        datasetTypes: { type: Array as any, required: true },
        transformationDataset: { type: Object as any, required: true },
        scriptTypes: { type: Array as any, required: true },
        dataSources: { type: Array as any, required: true },
        businessModels: { type: Array as any, required: true },
        pythonEnvironments: { type: Array as any, required: true },
        rEnvironments: { type: Array as any, required: true },
        metaSourceResource: { type: Array as any, required: true },
        availableTags: { type: Array as any, required: true },
        datasetToCloneId: { type: Number as any }
    },
    computed: {
        ...mapState({
            user: 'user',
            isEnterprise: 'isEnterprise'
        }),
        buttonDisabled(): any {
            return this.v$.$invalid
        }
    },
    emits: ['close', 'touched', 'loadingOlderVersion', 'olderVersionLoaded', 'updated', 'created', 'showSavingSpinner', 'hideSavingSpinner'],
    data() {
        return {
            detailViewDescriptor,
            v$: useValidate() as any,
            tablesToAdd: [] as any,
            tablesToRemove: [] as any,
            selectedDataset: {} as any,
            previewDataset: {} as any,
            selectedDatasetVersions: [] as any,
            filteredDatasetTypes: [] as any,
            scheduling: {
                repeatInterval: null as String | null
            } as any,
            touched: false,
            loading: false,
            loadingVersion: false,
            showPreviewDialog: false,
            showMetadataQueryInfo: false,
            activeTab: 0,
            qbeVisible: false,
            menuButtons: [] as any,
            showMonitoringDialog: false,
            showDataPreparation: false
        }
    },
    created() {
        this.getAllDatasetData()
    },
    watch: {
        id() {
            this.getAllDatasetData()
            this.activeTab = 0
        },
        datasetToCloneId() {
            this.cloneDatasetConfirm(this.datasetToCloneId)
        },
        datasetTypes() {
            this.filteredDatasetTypes = this.datasetTypes
        }
    },
    validations() {},
    methods: {
        //#region ===================== Get All Data ====================================================
        async getSelectedDataset() {
            await this.$http
                .get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/datasets/dataset/id/${this.id}`)
                .then((response: AxiosResponse<any>) => {
                    this.selectedDataset = response.data[0] ? { ...response.data[0] } : {}

                    this.selectedDataset.restJsonPathAttributes ? (this.selectedDataset.restJsonPathAttributes = JSON.parse(this.selectedDataset.restJsonPathAttributes ? this.selectedDataset.restJsonPathAttributes : '[]')) : []
                    this.selectedDataset.restRequestHeaders ? (this.selectedDataset.restRequestHeaders = JSON.parse(this.selectedDataset.restRequestHeaders ? this.selectedDataset.restRequestHeaders : '{}')) : {}

                    if (this.selectedDataset.restRequestHeaders) {
                        const restRequestHeadersKeys = Object.keys(this.selectedDataset.restRequestHeaders)
                        this.selectedDataset.restRequestHeaders = restRequestHeadersKeys.map((e) => ({ name: e, value: this.selectedDataset.restRequestHeaders[e] }))
                    }

                    this.selectedDataset.pythonEnvironment ? (this.selectedDataset.pythonEnvironment = JSON.parse(this.selectedDataset.pythonEnvironment ? this.selectedDataset.pythonEnvironment : '{}')) : ''
                })
                .catch()
        },
        async getSelectedDatasetVersions() {
            await this.$http
                .get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/datasets/olderversions/${this.id}`)
                .then((response: AxiosResponse<any>) => {
                    response.data.root ? (this.selectedDatasetVersions = response.data.root) : (this.selectedDatasetVersions = [])
                })
                .catch()
                .finally(() => (this.loading = false))
        },
        async getAllDatasetData() {
            if (this.id) {
                this.loading = true
                await this.getSelectedDataset()
                await this.getSelectedDatasetVersions()
                this.insertCurrentVersion()
            } else {
                this.selectedDataset = { ...detailViewDescriptor.newDataset }
                this.selectedDatasetVersions = []
            }
            this.filteredDatasetTypes = this.datasetTypes
        },
        insertCurrentVersion() {
            if (this.selectedDatasetVersions.length === 0) {
                const selectedType = this.datasetTypes.find((type) => type.VALUE_CD === this.selectedDataset.dsTypeCd)
                this.selectedDatasetVersions.push({ type: selectedType.VALUE_DS, userIn: this.selectedDataset.owner, versNum: 0, dateIn: this.selectedDataset.dateIn, dsId: this.selectedDataset.id })
            }
        },
        //#endregion ===============================================================================================

        //#region ===================== Clone Functionality ====================================================
        cloneDatasetConfirm(datasetId) {
            this.$confirm.require({
                icon: 'pi pi-exclamation-triangle',
                message: this.$t('kpi.kpiDefinition.confirmClone'),
                header: this.$t(' '),
                accept: () => this.cloneDataset(datasetId)
            })
        },
        async cloneDataset(datasetId) {
            await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/datasets/dataset/id/${datasetId}`).then(async (response: AxiosResponse<any>) => {
                if (response.data[0].dsTypeCd === 'File') {
                    await this.$http.put(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/datasets/clone-file?fileName=${response.data[0].fileName}`)
                }
                delete response.data[0].id
                response.data[0].label = '...'
                response.data[0].dsVersions = []
                response.data[0].usedByNDocs = 0

                this.selectedDataset = { ...response.data[0] }

                this.selectedDataset.restJsonPathAttributes ? (this.selectedDataset.restJsonPathAttributes = JSON.parse(this.selectedDataset.restJsonPathAttributes ? this.selectedDataset.restJsonPathAttributes : '[]')) : []
                this.selectedDataset.restRequestHeaders ? (this.selectedDataset.restRequestHeaders = JSON.parse(this.selectedDataset.restRequestHeaders ? this.selectedDataset.restRequestHeaders : '{}')) : {}

                if (this.selectedDataset.restRequestHeaders) {
                    const restRequestHeadersKeys = Object.keys(this.selectedDataset.restRequestHeaders)
                    this.selectedDataset.restRequestHeaders = restRequestHeadersKeys.map((e) => ({ name: e, value: this.selectedDataset.restRequestHeaders[e] }))
                }
            })
        },
        //#endregion ===============================================================================================

        //#region ===================== Save/Update Dataset & Tags =================================================
        async saveDataset() {
            let dsToSave = { ...this.selectedDataset } as any
            if (this.user?.functionalities?.includes('DataPreparation') && dsToSave.id) {
                await this.$http
                    .get(process.env.VUE_APP_DATA_PREPARATION_PATH + '1.0/instance/dataset/' + dsToSave.id, { headers: { 'X-Disable-Interceptor': 'true' } })
                    .then((response: AxiosResponse<any>) => {
                        if (response.data) {
                            this.$confirm.require({
                                icon: 'pi pi-exclamation-triangle',
                                message: this.$t('managers.datasetManagement.dataPreparation.datasetInvolvedIntoDataPrep'),
                                header: this.$t('managers.datasetManagement.saveTitle'),
                                accept: () => this.checkDerived(dsToSave)
                            })
                        } else {
                            this.checkDerived(dsToSave)
                        }
                    })
                    .catch((err) => {
                        this.$store.commit('setError', { title: this.$t('common.toast.errorTitle'), msg: this.$t(err.data.errors[0].message) })
                    })
            } else {
                this.checkDerived(dsToSave)
            }
        },
        async checkDerived(dsToSave) {
            await this.$http
                .get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + '1.0/datasets/dataset/' + dsToSave.label + '/derived', { headers: { 'X-Disable-Interceptor': 'true' } })
                .then((response: AxiosResponse<any>) => {
                    if (response.data) {
                        this.$confirm.require({
                            icon: 'pi pi-exclamation-triangle',
                            message: this.$t('managers.datasetManagement.derived.checkForExistingDerivedDatasets'),
                            header: this.$t('managers.datasetManagement.saveTitle'),
                            accept: () => this.proceedOnSaving(dsToSave)
                        })
                    } else {
                        this.proceedOnSaving(dsToSave)
                    }
                })
                .catch((err) => {
                    this.$store.commit('setError', { title: this.$t('common.toast.errorTitle'), msg: this.$t(err.data.errors[0].message) })
                })
        },
        async proceedOnSaving(dsToSave) {
            this.$emit('showSavingSpinner')
            if (dsToSave.dsTypeCd.toLowerCase() == 'rest' || dsToSave.dsTypeCd.toLowerCase() == 'solr') {
                dsToSave.restRequestHeaders = (dsToSave.restRequestHeaders || []).reduce((acc, curr) => {
                    acc[curr['name']] = curr['value']
                    return acc
                }, {})
            }

            dsToSave.pars ? '' : (dsToSave.pars = [])
            dsToSave.pythonEnvironment ? (dsToSave.pythonEnvironment = JSON.stringify(dsToSave.pythonEnvironment)) : ''

            dsToSave.meta ? (dsToSave.meta = await this.manageDatasetFieldMetadata(dsToSave.meta)) : (dsToSave.meta = [])
            dsToSave.recalculateMetadata = true

            dsToSave.isScheduled ? (dsToSave.schedulingCronLine = await this.formatCronForSave()) : ''

            await this.$http
                .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/datasets/`, dsToSave, {
                    headers: {
                        Accept: 'application/json, text/plain, */*',
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                })
                .then(async (response: AxiosResponse<any>) => {
                    this.touched = false
                    this.$store.commit('setInfo', { title: this.$t('common.toast.createTitle'), msg: this.$t('common.toast.success') })
                    this.selectedDataset.id ? this.$emit('updated') : this.$emit('created', response)

                    await this.saveTags(dsToSave, response.data.id)
                    if (this.user.functionalities.includes('SchedulingDatasetManagement')) {
                        await this.saveSchedulation(dsToSave, response.data.id)
                    }
                    await this.saveLinks(response.data.id)
                    await this.removeLinks(response.data.id)
                    await this.getSelectedDataset()
                })
                .catch()
                .finally(() => this.$emit('hideSavingSpinner'))
        },

        async saveTags(dsToSave, id) {
            let tags = {} as any
            tags.versNum = dsToSave.versNum + 1
            tags.tagsToAdd = dsToSave.tags

            await this.$http
                .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/datasets/${id}/dstags/`, tags, {
                    headers: {
                        Accept: 'application/json, text/plain, */*',
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                })
                .catch()
        },
        async saveSchedulation(dsToSave, id) {
            if (dsToSave.isScheduled) {
                await this.$http
                    .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `scheduleree/persistence/dataset/id/${id}`, dsToSave, {
                        headers: {
                            Accept: 'application/json, text/plain, */*',
                            'Content-Type': 'application/json;charset=UTF-8'
                        }
                    })
                    .catch()
            } else {
                await this.$http.delete(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `scheduleree/persistence/dataset/label/${dsToSave.label}`).catch()
            }
        },
        async saveLinks(id) {
            if (this.tablesToAdd.length > 0) {
                this.tablesToAdd.forEach(async (link) => {
                    if (link.added === true) {
                        delete link.added
                        await this.$http
                            .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/metaDsRelationResource/${id}`, link, {
                                headers: { Accept: 'application/json, text/plain, */*', 'Content-Type': 'application/json;charset=UTF-8' }
                            })
                            .catch()
                    }
                })
            }
        },
        async removeLinks(id) {
            if (this.tablesToRemove.length > 0) {
                this.tablesToRemove.forEach(async (link) => {
                    if (link.deleted === true) {
                        await this.$http
                            .delete(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/metaDsRelationResource/${id}/${link.tableId}`, {
                                headers: { Accept: 'application/json, text/plain, */*', 'Content-Type': 'application/json;charset=UTF-8' }
                            })
                            .catch()
                    }
                })
            }
        },
        async manageDatasetFieldMetadata(fieldsColumns) {
            if (fieldsColumns.columns != undefined && fieldsColumns.columns != null) {
                var columnsArray = new Array()
                var columnsNames = new Array()

                for (var i = 0; i < fieldsColumns.columns.length; i++) {
                    var element = fieldsColumns.columns[i]
                    columnsNames.push(element.column)
                }

                columnsNames = this.removeDuplicates(columnsNames)

                for (i = 0; i < columnsNames.length; i++) {
                    var columnObject = { displayedName: '', name: '', fieldType: '', type: '', personal: false, decrypt: false, subjectId: false }
                    var currentColumnName = columnsNames[i]

                    //remove the part before the double dot if the column is in the format ex: it.eng.spagobi.Customer:customerId
                    if (currentColumnName.indexOf(':') != -1) {
                        var arr = currentColumnName.split(':')
                        columnObject.displayedName = arr[1]
                    } else {
                        columnObject.displayedName = currentColumnName
                    }

                    columnObject.name = currentColumnName
                    for (var j = 0; j < fieldsColumns.columns.length; j++) {
                        element = fieldsColumns.columns[j]
                        if (element.column == currentColumnName) {
                            if (element.pname.toUpperCase() == 'type'.toUpperCase()) {
                                columnObject.type = element.pvalue
                            } else if (element.pname.toUpperCase() == 'fieldType'.toUpperCase()) {
                                columnObject.fieldType = element.pvalue
                            } else if (element.pname.toUpperCase() == 'personal'.toUpperCase()) {
                                columnObject.personal = element.pvalue
                            } else if (element.pname.toUpperCase() == 'decrypt'.toUpperCase()) {
                                columnObject.decrypt = element.pvalue
                            } else if (element.pname.toUpperCase() == 'subjectId'.toUpperCase()) {
                                columnObject.subjectId = element.pvalue
                            }
                        }
                    }
                    columnsArray.push(columnObject)
                }

                return columnsArray
            }
        },
        checkFormulaForParams() {
            if (this.selectedDataset?.query?.includes('${') && this.selectedDataset?.isPersisted) {
                this.$store.commit('setError', { title: this.$t('common.toast.errorTitle'), msg: this.$t('managers.datasetManagement.formulaParamError') })
            } else this.saveDataset()
        },
        removeDuplicates(array) {
            var index = {}
            for (var i = array.length - 1; i >= 0; i--) {
                if (array[i] in index) {
                    array.splice(i, 1)
                } else {
                    index[array[i]] = true
                }
            }
            return array
        },
        async formatCronForSave() {
            if (this.selectedDataset.isScheduled) {
                if (this.selectedDataset.startDate == null) {
                    this.selectedDataset.startDate = new Date()
                }
                var repeatInterval = this.scheduling.repeatInterval
                var finalCronString = ''
                var secondsForCron = 0
                var minutesForCron = this.stringifySchedulingValues(this.scheduling.minutesSelected && this.scheduling.minutesSelected.length != 0, 'minutesSelected')
                var hoursForCron = this.stringifySchedulingValues(repeatInterval != 'minute' && this.scheduling.hoursSelected && this.scheduling.hoursSelected.length != 0, 'hoursSelected')
                var daysForCron = this.stringifySchedulingValues((repeatInterval === 'day' || repeatInterval === 'month') && this.scheduling.daysSelected && this.scheduling.daysSelected.length != 0, 'daysSelected')
                var monthsForCron = this.stringifySchedulingValues(repeatInterval === 'month' && this.scheduling.monthsSelected && this.scheduling.monthsSelected.length != 0, 'monthsSelected')
                var weekdaysForCron = this.stringifySchedulingValues(repeatInterval === 'week' && this.scheduling.weekdaysSelected && this.scheduling.weekdaysSelected.length != 0, 'weekdaysSelected')

                if (daysForCron == '*' && weekdaysForCron != '*') {
                    daysForCron = '?'
                } else {
                    weekdaysForCron = '?'
                }
                finalCronString = minutesForCron + ' ' + hoursForCron + ' ' + daysForCron + ' ' + monthsForCron + ' ' + weekdaysForCron

                return secondsForCron + ' ' + finalCronString
            }
        },
        stringifySchedulingValues(condition, selectedValue) {
            var stringValue = ''
            if (condition) {
                for (var i = 0; i < this.scheduling[selectedValue].length; i++) {
                    stringValue += '' + this.scheduling[selectedValue][i]

                    if (i < this.scheduling[selectedValue].length - 1) {
                        stringValue += ','
                    }
                }
                return stringValue
            } else {
                stringValue = '*'
                return stringValue
            }
        },
        //#endregion ===============================================================================================

        async sendDatasetForPreview() {
            if (this.selectedDataset.dsTypeCd === 'Solr') {
                this.previewDataset = JSON.parse(JSON.stringify(this.selectedDataset))
                let restRequestHeadersTemp = {}
                if (this.previewDataset.dsTypeCd.toLowerCase() == 'rest' || this.previewDataset.dsTypeCd.toLowerCase() == 'solr') {
                    for (let i = 0; i < this.previewDataset.restRequestHeaders.length; i++) {
                        restRequestHeadersTemp[this.previewDataset.restRequestHeaders[i]['name']] = this.previewDataset.restRequestHeaders[i]['value']
                    }
                }
                this.previewDataset['restRequestHeaders'] = JSON.stringify(restRequestHeadersTemp)
                this.previewDataset['restJsonPathAttributes'] && this.previewDataset['restJsonPathAttributes'].length > 0 ? (this.previewDataset.restJsonPathAttributes = JSON.stringify(this.previewDataset.restJsonPathAttributes)) : (this.previewDataset.restJsonPathAttributes = [])
                this.previewDataset.pars ? '' : (this.previewDataset.pars = [])
                this.previewDataset.pythonEnvironment ? (this.previewDataset.pythonEnvironment = JSON.stringify(this.previewDataset.pythonEnvironment)) : ''
                this.previewDataset.meta ? (this.previewDataset.meta = await this.manageDatasetFieldMetadata(this.previewDataset.meta)) : (this.previewDataset.meta = [])

                this.showPreviewDialog = true
            } else {
                this.previewDataset = this.selectedDataset
                this.showPreviewDialog = true
            }
        },

        onAddLinkedTables(event) {
            this.tablesToAdd = event
            this.$emit('touched')
        },
        onRemoveLinkedTables(event) {
            this.tablesToRemove = event
            this.$emit('touched')
        },
        onOlderVersionLoaded(event) {
            this.$emit('olderVersionLoaded')
            this.selectedDataset = { ...event }
        },
        toggleMenu(event: Event, dataset: any): void {
            this.menuButtons = [] as any

            if (this.isOpenInQBEVisible(this.selectedDataset)) {
                this.menuButtons.push({
                    key: 1,
                    label: this.$t('workspace.myModels.openInQBE'),
                    icon: 'fas fa-file-circle-question',
                    command: () => {
                        this.openDatasetInQbe()
                    }
                })
            }

            if (dataset.pars && dataset.pars?.length == 0) {
                this.menuButtons.push({
                    key: 2,
                    label: this.$t('managers.datasetManagement.openDP'),
                    icon: 'fas fa-cogs',
                    command: () => {
                        this.showDataPreparation = true
                    }
                })
            }
            if (dataset.dsTypeCd == 'Prepared') {
                this.menuButtons.push({
                    key: 3,
                    label: this.$t('managers.datasetManagement.monitoring'),
                    icon: 'pi pi-chart-line',
                    command: () => {
                        this.showMonitoringDialog = true
                    }
                })
            }

            // eslint-disable-next-line
            // @ts-ignore
            this.$refs.optionsMenu.toggle(event)
        },
        isOpenInQBEVisible(dataset: any) {
            return dataset.pars?.length == 0 && ((dataset.isPersisted && dataset.dsTypeCd == 'File') || dataset.dsTypeCd == 'Query' || dataset.dsTypeCd == 'Flat')
        },
        openDatasetInQbe() {
            this.qbeVisible = true
        },
        closeQbe() {
            this.qbeVisible = false
        }
    }
})
